import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  HiShieldCheck,
  HiPlus,
  HiMagnifyingGlass,
  HiInformationCircle,
  HiLockClosed,
  HiUserGroup,
  HiCog6Tooth,
  HiArrowRightOnRectangle,
  HiCheck,
  HiXMark,
  HiChevronUp,
  HiChevronDown,
  HiTrash,
  HiPencil,
  HiArrowPath,
  HiExclamationTriangle,
  HiCheckCircle,
  HiXCircle,
} from 'react-icons/hi2';
import { useAuthStore } from '@/stores/authStore';
import './ClansPanel.css';

// ============================================================================
// MADBOAT ALERT SYSTEM - Custom styled alerts
// ============================================================================
type AlertType = 'info' | 'success' | 'warning' | 'error' | 'confirm';

interface AlertState {
  show: boolean;
  type: AlertType;
  title: string;
  message: string;
  onConfirm?: () => void;
  onCancel?: () => void;
}

const initialAlertState: AlertState = {
  show: false,
  type: 'info',
  title: '',
  message: '',
};

function MadBoatAlert({ alert, onClose }: { alert: AlertState; onClose: () => void }) {
  const { t } = useTranslation();
  if (!alert.show) return null;

  const icons = {
    info: <HiInformationCircle size={32} />,
    success: <HiCheckCircle size={32} />,
    warning: <HiExclamationTriangle size={32} />,
    error: <HiXCircle size={32} />,
    confirm: <HiExclamationTriangle size={32} />,
  };

  const handleConfirm = () => {
    alert.onConfirm?.();
    onClose();
  };

  const handleCancel = () => {
    alert.onCancel?.();
    onClose();
  };

  return (
    <div className="madboat-alert-overlay" onClick={alert.type !== 'confirm' ? onClose : undefined}>
      <div className="madboat-alert-container" onClick={(e) => e.stopPropagation()}>
        <div className={`madboat-alert-icon ${alert.type}`}>
          {icons[alert.type]}
        </div>
        <h3 className="madboat-alert-title">{alert.title}</h3>
        <p className="madboat-alert-message">{alert.message}</p>
        <div className="madboat-alert-actions">
          {alert.type === 'confirm' ? (
            <>
              <button className="madboat-alert-btn cancel" onClick={handleCancel}>
                {t('clans.alert.cancel')}
              </button>
              <button className="madboat-alert-btn confirm" onClick={handleConfirm}>
                {t('clans.alert.confirm')}
              </button>
            </>
          ) : (
            <button className="madboat-alert-btn primary" onClick={onClose}>
              {t('clans.alert.close')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

interface Clan {
  id: string;
  name: string;
  tag: string;
  description: string;
  leader_id: string;
  member_count: number;
  max_members: number;
  level: number;
  xp: number;
  recruitment_open: boolean;
  my_role?: string;
  my_contribution_xp?: number;
}

interface ClanMember {
  id: string;
  user_id: string;
  username: string;
  display_name: string;
  role: 'leader' | 'officer' | 'member';
  odyssey_level: number;
  odyssey_title: string;
  contribution_xp: number;
  joined_at: number;
}

interface JoinRequest {
  id: string;
  user_id: string;
  username: string;
  display_name: string;
  odyssey_level: number;
  odyssey_title: string;
  message: string;
  created_at: number;
}

type ClanTab = 'overview' | 'members' | 'requests' | 'settings';

interface RecruitmentPost {
  id: string;
  clan_id: string;
  clan_name: string;
  clan_tag: string;
  clan_level: number;
  member_count: number;
  max_members: number;
  title: string;
  description: string;
  requirements: string;
  discord_link: string;
  posted_by_username: string;
  created_at: number;
}

interface ClansPanelProps {
  userTitle: string; // User's odyssey title
  userLevel: number; // User's odyssey level
  onOpenRecruitmentModal?: (posts: RecruitmentPost[]) => void; // Open external modal
}

export function ClansPanel({ userTitle, userLevel, onOpenRecruitmentModal }: ClansPanelProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [myClan, setMyClan] = useState<Clan | null>(null);
  const [isMember, setIsMember] = useState(false);
  const [recruitmentPosts, setRecruitmentPosts] = useState<RecruitmentPost[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showRecruitmentModal, setShowRecruitmentModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const { token, user } = useAuthStore();

  // MadBoat Alert System
  const [alertState, setAlertState] = useState<AlertState>(initialAlertState);

  const showAlert = useCallback((type: AlertType, title: string, message: string, onConfirm?: () => void) => {
    setAlertState({
      show: true,
      type,
      title,
      message,
      onConfirm,
      onCancel: () => {},
    });
  }, []);

  const closeAlert = useCallback(() => {
    setAlertState(initialAlertState);
  }, []);

  // Check if user has clan access
  // For now, any authenticated user has access (tier check is done on backend)
  // TODO: Re-enable tier check when frontend properly fetches mkey_type
  const hasClanAccess = !!token && !!user;

  // Check if user can create clan (Capitão or higher)
  const canCreateClan = ['Capitão', 'Almirante'].includes(userTitle);

  // Load user's clan
  const loadMyClan = async () => {
    if (!token) return;

    try {
      const response = await fetch('https://mkey-api-production.fidelismadboat.workers.dev/clans/my', {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setMyClan(data.clan);
        setIsMember(data.is_member);
      }
    } catch (error) {
      console.error('[ClansPanel] Error loading clan:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load recruitment posts
  const loadRecruitmentPosts = async () => {
    if (!token) return;

    try {
      const response = await fetch('https://mkey-api-production.fidelismadboat.workers.dev/clans/recruitment', {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setRecruitmentPosts(data.posts || []);
      }
    } catch (error) {
      console.error('[ClansPanel] Error loading recruitment posts:', error);
    }
  };

  // Create clan
  const handleCreateClan = async (name: string, tag: string, description: string) => {
    if (!token) return;

    try {
      const response = await fetch('https://mkey-api-production.fidelismadboat.workers.dev/clans', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, tag, description }),
      });

      if (response.ok) {
        const data = await response.json();
        setMyClan(data.clan);
        setIsMember(true);
        setShowCreateForm(false);
        showAlert('success', t('clans.clanCreated'), t('clans.clanCreatedMsg'));
      } else {
        const error = await response.json();
        showAlert('error', t('clans.errorGeneric'), error.message || error.error || t('clans.createError'));
      }
    } catch (error) {
      console.error('[ClansPanel] Error creating clan:', error);
      showAlert('error', t('clans.errorGeneric'), t('clans.connectionError'));
    }
  };

  // Request to join clan
  const handleJoinClan = async (clanId: string, clanName: string) => {
    if (!token) return;

    showAlert('confirm', t('clans.joinClanTitle'), t('clans.joinClanConfirm', { name: clanName }), async () => {
      try {
        const response = await fetch(`https://mkey-api-production.fidelismadboat.workers.dev/clans/${clanId}/join`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ message: '' }),
        });

        if (response.ok) {
          showAlert('success', t('clans.requestSentTitle'), t('clans.requestSentMsg'));
        } else {
          const error = await response.json();
          showAlert('error', t('clans.errorGeneric'), error.error || t('clans.joinError'));
        }
      } catch (error) {
        console.error('[ClansPanel] Error joining clan:', error);
        showAlert('error', t('clans.errorGeneric'), t('clans.connectionError'));
      }
    });
  };

  useEffect(() => {
    loadMyClan();
    loadRecruitmentPosts();
  }, [token]);

  if (loading) {
    return (
      <div className="clans-panel">
        <div className="loading-state">{t('clans.loading')}</div>
      </div>
    );
  }

  // User doesn't have tier 2+ - show upgrade prompt
  if (!hasClanAccess) {
    return (
      <div className="clans-panel">
        <div className="clan-upgrade-prompt">
          {/* Pulsing background circles */}
          <div className="upgrade-pulse-circles">
            <div className="upgrade-pulse pulse-1"></div>
            <div className="upgrade-pulse pulse-2"></div>
            <div className="upgrade-pulse pulse-3"></div>
          </div>

          {/* Glow effect */}
          <div className="upgrade-glow"></div>

          {/* Content */}
          <div className="upgrade-content">
            <div className="upgrade-icon">
              <HiLockClosed size={32} />
            </div>
            <h3 className="upgrade-title">{t('clans.upgradeTitle')}</h3>
            <p className="upgrade-text">
              {t('clans.upgradeText')}
            </p>
            <p className="upgrade-requirement" dangerouslySetInnerHTML={{ __html: t('clans.upgradeRequirement') }} />
            <button
              className="btn-upgrade-clan"
              onClick={() => navigate('/pricing')}
            >
              {t('clans.upgradeBtn')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // User is already in a clan - show clan dashboard
  if (isMember && myClan) {
    return (
      <>
        <ClanDashboard
          clan={myClan}
          token={token!}
          userId={(user as any)?.id}
          onLeaveClan={() => {
            setMyClan(null);
            setIsMember(false);
          }}
          onClanUpdated={loadMyClan}
          showAlert={showAlert}
        />
        <MadBoatAlert alert={alertState} onClose={closeAlert} />
      </>
    );
  }

  // Handle create clan click
  const handleCreateClanClick = () => {
    if (!canCreateClan) {
      showAlert(
        'warning',
        t('clans.rankInsufficient'),
        t('clans.rankInsufficientMsg', { title: userTitle, level: userLevel })
      );
      return;
    }
    setShowCreateForm(true);
  };

  // User is NOT in a clan - show clan hub (CTA focused)
  return (
    <div className="clans-panel">
      {/* Clan Hub - Button Focused Layout */}
      <div className="clan-hub">
        {/* Primary CTA - Create Clan (HERO) */}
        <div className="clan-cta-hero">
          {/* Pulsing circles around button */}
          <div className="cta-pulse-circles">
            <div className="cta-pulse pulse-1"></div>
            <div className="cta-pulse pulse-2"></div>
            <div className="cta-pulse pulse-3"></div>
          </div>

          {/* Glow effect */}
          <div className="cta-glow"></div>

          {/* The Button */}
          <button
            className="btn-create-clan-hero"
            onClick={handleCreateClanClick}
          >
            <HiShieldCheck size={24} />
            <span>{t('clans.createClan')}</span>
          </button>
        </div>

        {/* Secondary - Buscar Clã */}
        <button
          className="btn-buscar-clan"
          onClick={() => {
            // Use external modal if available, otherwise internal
            if (onOpenRecruitmentModal) {
              onOpenRecruitmentModal(recruitmentPosts);
            } else {
              setShowRecruitmentModal(true);
            }
          }}
        >
          <HiMagnifyingGlass size={14} />
          {t('clans.searchClan')}
        </button>
      </div>

      {/* Info Panel (collapsible) */}
      {showInfo && (
        <div className="clan-info-panel">
          <h4>{t('clans.infoTitle')}</h4>
          <p dangerouslySetInnerHTML={{ __html: t('clans.infoDesc') }} />

          <h5>{t('clans.infoBenefitsTitle')}</h5>
          <ul>
            <li>{t('clans.infoBenefit1')}</li>
            <li>{t('clans.infoBenefit2')}</li>
            <li>{t('clans.infoBenefit3')}</li>
            <li>{t('clans.infoBenefit4')}</li>
          </ul>

          <h5>{t('clans.infoCreateTitle')}</h5>
          <p dangerouslySetInnerHTML={{ __html: t('clans.infoCreateDesc') }} />
          <ul>
            <li>{t('clans.infoCreateReq1')}</li>
            <li>{t('clans.infoCreateReq2')}</li>
            <li>{t('clans.infoCreateReq3')}</li>
          </ul>

          <h5>{t('clans.infoJoinTitle')}</h5>
          <p dangerouslySetInnerHTML={{ __html: t('clans.infoJoinDesc') }} />
        </div>
      )}

      {/* Create Clan Form */}
      {showCreateForm && (
        <CreateClanForm
          onSubmit={handleCreateClan}
          onCancel={() => setShowCreateForm(false)}
        />
      )}

      {/* Recruitment Modal (Slide-in Lateral) */}
      {showRecruitmentModal && (
        <RecruitmentModal
          posts={recruitmentPosts}
          onClose={() => setShowRecruitmentModal(false)}
          onJoinClan={handleJoinClan}
        />
      )}

      {/* MadBoat Alert System */}
      <MadBoatAlert alert={alertState} onClose={closeAlert} />
    </div>
  );
}

// Recruitment Modal Component - Slide-in Lateral
interface RecruitmentModalProps {
  posts: RecruitmentPost[];
  onClose: () => void;
  onJoinClan: (clanId: string, clanName: string) => void;
}

function RecruitmentModal({ posts, onClose, onJoinClan }: RecruitmentModalProps) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');

  const filteredPosts = posts.filter(post =>
    post.clan_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.clan_tag.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="recruitment-slide-overlay" onClick={onClose}>
      <div className="recruitment-slide-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="recruitment-slide-header">
          <div className="header-title">
            <HiMagnifyingGlass size={24} />
            <h2>{t('clans.searchClan')}</h2>
          </div>
          <button className="btn-close-slide" onClick={onClose}>
            <HiXMark size={24} />
          </button>
        </div>

        {/* Search Bar */}
        <div className="recruitment-search">
          <HiMagnifyingGlass size={18} className="search-icon" />
          <input
            type="text"
            placeholder={t('clans.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
          />
        </div>

        {/* Results */}
        <div className="recruitment-slide-body">
          {filteredPosts.length === 0 ? (
            <div className="empty-search">
              <HiShieldCheck size={64} className="empty-icon" />
              <h3>{t('clans.noClanFound')}</h3>
              <p>
                {posts.length === 0
                  ? t('clans.noClanRecruiting')
                  : t('clans.tryOtherTerms')}
              </p>
            </div>
          ) : (
            <div className="recruitment-grid">
              {filteredPosts.map(post => (
                <div key={post.id} className="recruitment-card-large">
                  <div className="card-top">
                    <div className="clan-emblem-large">
                      <HiShieldCheck size={36} />
                    </div>
                    <div className="clan-info-large">
                      <h3 className="clan-name-large">
                        <span className="clan-tag-badge">[{post.clan_tag}]</span>
                        {post.clan_name}
                      </h3>
                      <div className="clan-meta-large">
                        <span className="meta-item">
                          <HiUserGroup size={14} />
                          {post.member_count}/{post.max_members}
                        </span>
                        <span className="meta-item level">{t('clans.levelShort', { level: post.clan_level })}</span>
                      </div>
                    </div>
                  </div>

                  <h4 className="post-title-large">{post.title}</h4>
                  <p className="post-description-large">{post.description}</p>

                  {post.requirements && (
                    <div className="requirements-box">
                      <strong>{t('clans.requirements')}</strong>
                      <span>{post.requirements}</span>
                    </div>
                  )}

                  <div className="card-actions">
                    {post.discord_link && (
                      <a
                        href={post.discord_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-discord"
                      >
                        Discord
                      </a>
                    )}
                    <button
                      className="btn-request-join"
                      onClick={() => onJoinClan(post.clan_id, post.clan_name)}
                    >
                      {t('clans.requestJoin')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="recruitment-slide-footer">
          <span>{t('clans.clansFound', { count: filteredPosts.length } as any)}</span>
        </div>
      </div>
    </div>
  );
}

// Create Clan Form Component
interface CreateClanFormProps {
  onSubmit: (name: string, tag: string, description: string) => void;
  onCancel: () => void;
}

function CreateClanForm({ onSubmit, onCancel }: CreateClanFormProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [tag, setTag] = useState('');
  const [description, setDescription] = useState('');
  const [formError, setFormError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!name.trim() || !tag.trim()) {
      setFormError(t('clans.createForm.errorRequired'));
      return;
    }

    // Validate tag
    const tagRegex = /^[A-Z]{3,5}$/;
    if (!tagRegex.test(tag)) {
      setFormError(t('clans.createForm.errorTagFormat'));
      return;
    }

    onSubmit(name.trim(), tag.trim().toUpperCase(), description.trim());
  };

  return (
    <form className="create-clan-form" onSubmit={handleSubmit}>
      <h3>{t('clans.createForm.title')}</h3>

      {formError && (
        <div className="form-error">
          <HiExclamationTriangle size={16} />
          {formError}
        </div>
      )}

      <div className="form-group">
        <label>{t('clans.createForm.nameLabel')}</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('clans.createForm.namePlaceholder')}
          maxLength={30}
        />
      </div>

      <div className="form-group">
        <label>{t('clans.createForm.tagLabel')}</label>
        <input
          type="text"
          value={tag}
          onChange={(e) => setTag(e.target.value.toUpperCase())}
          placeholder={t('clans.createForm.tagPlaceholder')}
          maxLength={5}
          style={{ textTransform: 'uppercase' }}
        />
        <small>{t('clans.createForm.tagHint')}</small>
      </div>

      <div className="form-group">
        <label>{t('clans.createForm.descLabel')}</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('clans.createForm.descPlaceholder')}
          maxLength={200}
          rows={3}
        />
      </div>

      <div className="form-actions">
        <button type="button" className="btn-cancel" onClick={onCancel}>
          {t('clans.createForm.cancel')}
        </button>
        <button type="submit" className="btn-submit">
          {t('clans.createForm.submit')}
        </button>
      </div>
    </form>
  );
}

// ============================================================================
// CLAN DASHBOARD - Full clan management interface
// ============================================================================
interface ClanDashboardProps {
  clan: Clan;
  token: string;
  userId: string;
  onLeaveClan: () => void;
  onClanUpdated: () => void;
  showAlert: (type: AlertType, title: string, message: string, onConfirm?: () => void) => void;
}

const API_BASE = 'https://mkey-api-production.fidelismadboat.workers.dev';

function ClanDashboard({ clan, token, userId, onLeaveClan, onClanUpdated, showAlert }: ClanDashboardProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<ClanTab>('overview');
  const [members, setMembers] = useState<ClanMember[]>([]);
  const [requests, setRequests] = useState<JoinRequest[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loadingRequests, setLoadingRequests] = useState(false);

  const isLeader = clan.my_role === 'leader';
  const isOfficer = clan.my_role === 'officer';
  const canManage = isLeader || isOfficer;

  // Load members
  const loadMembers = async () => {
    setLoadingMembers(true);
    try {
      const res = await fetch(`${API_BASE}/clans/${clan.id}/members`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMembers(data.members || []);
      }
    } catch (err) {
      console.error('[ClanDashboard] Error loading members:', err);
    } finally {
      setLoadingMembers(false);
    }
  };

  // Load requests
  const loadRequests = async () => {
    if (!canManage) return;
    setLoadingRequests(true);
    try {
      const res = await fetch(`${API_BASE}/clans/${clan.id}/requests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRequests(data.requests || []);
      }
    } catch (err) {
      console.error('[ClanDashboard] Error loading requests:', err);
    } finally {
      setLoadingRequests(false);
    }
  };

  // Handle join request
  const handleRequest = async (requestId: string, action: 'approve' | 'reject') => {
    try {
      const res = await fetch(`${API_BASE}/clans/${clan.id}/requests/${requestId}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action }),
      });
      if (res.ok) {
        setRequests((prev) => prev.filter((r) => r.id !== requestId));
        if (action === 'approve') {
          onClanUpdated();
          loadMembers();
          showAlert('success', t('clans.dashboard.memberApproved'), t('clans.dashboard.memberApprovedMsg'));
        }
      } else {
        const err = await res.json();
        showAlert('error', t('clans.errorGeneric'), err.error || t('clans.dashboard.errorProcessing'));
      }
    } catch (err) {
      console.error('[ClanDashboard] Error handling request:', err);
    }
  };

  // Change member role
  const handleChangeRole = async (memberId: string, newRole: 'officer' | 'member') => {
    if (!isLeader) return;
    try {
      const res = await fetch(`${API_BASE}/clans/${clan.id}/members/${memberId}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ role: newRole }),
      });
      if (res.ok) {
        loadMembers();
        showAlert('success', t('clans.dashboard.roleChanged'), newRole === 'officer' ? t('clans.dashboard.promoted') : t('clans.dashboard.demoted'));
      } else {
        const err = await res.json();
        showAlert('error', t('clans.errorGeneric'), err.error || t('clans.dashboard.errorChangingRole'));
      }
    } catch (err) {
      console.error('[ClanDashboard] Error changing role:', err);
    }
  };

  // Kick member
  const handleKick = (memberId: string, memberName: string) => {
    showAlert('confirm', t('clans.dashboard.kickTitle'), t('clans.dashboard.kickConfirm', { name: memberName }), async () => {
      try {
        const res = await fetch(`${API_BASE}/clans/${clan.id}/members/${memberId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          loadMembers();
          onClanUpdated();
          showAlert('success', t('clans.dashboard.memberKicked'), t('clans.dashboard.memberKickedMsg', { name: memberName }));
        } else {
          const err = await res.json();
          showAlert('error', t('clans.errorGeneric'), err.error || t('clans.dashboard.errorKicking'));
        }
      } catch (err) {
        console.error('[ClanDashboard] Error kicking member:', err);
      }
    });
  };

  // Leave clan
  const handleLeave = () => {
    const msg = isLeader
      ? t('clans.dashboard.dissolveConfirm')
      : t('clans.dashboard.leaveConfirm');
    const title = isLeader ? t('clans.dashboard.dissolveClan') : t('clans.dashboard.leaveClan');

    showAlert('confirm', title, msg, async () => {
      try {
        if (isLeader) {
          // Dissolve
          const res = await fetch(`${API_BASE}/clans/${clan.id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            onLeaveClan();
          } else {
            const err = await res.json();
            showAlert('error', t('clans.errorGeneric'), err.error || t('clans.dashboard.errorDissolving'));
          }
        } else {
          // Leave
          const res = await fetch(`${API_BASE}/clans/${clan.id}/leave`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            onLeaveClan();
          } else {
            const err = await res.json();
            showAlert('error', t('clans.errorGeneric'), err.error || t('clans.dashboard.errorLeaving'));
          }
        }
      } catch (err) {
        console.error('[ClanDashboard] Error leaving clan:', err);
      }
    });
  };

  // Transfer leadership
  const handleTransfer = (newLeaderId: string, newLeaderName: string) => {
    showAlert('confirm', t('clans.dashboard.transferTitle'), t('clans.dashboard.transferConfirm', { name: newLeaderName }), async () => {
      try {
        const res = await fetch(`${API_BASE}/clans/${clan.id}/transfer`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ new_leader_id: newLeaderId }),
        });
        if (res.ok) {
          onClanUpdated();
          loadMembers();
          showAlert('success', t('clans.dashboard.leadershipTransferred'), t('clans.dashboard.leadershipTransferredMsg', { name: newLeaderName }));
        } else {
          const err = await res.json();
          showAlert('error', t('clans.errorGeneric'), err.error || t('clans.dashboard.errorTransferring'));
        }
      } catch (err) {
        console.error('[ClanDashboard] Error transferring leadership:', err);
      }
    });
  };

  useEffect(() => {
    if (activeTab === 'members') loadMembers();
    if (activeTab === 'requests') loadRequests();
  }, [activeTab]);

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'leader':
        return <span className="role-badge leader">{t('clans.dashboard.roleLeader')}</span>;
      case 'officer':
        return <span className="role-badge officer">{t('clans.dashboard.roleOfficer')}</span>;
      default:
        return <span className="role-badge member">{t('clans.dashboard.roleMember')}</span>;
    }
  };

  return (
    <div className="clans-panel clan-dashboard">
      {/* Header */}
      <div className="clan-dashboard-header">
        <div className="clan-emblem">
          <HiShieldCheck size={32} />
        </div>
        <div className="clan-header-info">
          <h3 className="clan-name">[{clan.tag}] {clan.name}</h3>
          <p className="clan-meta">
            {t('clans.dashboard.level', { level: clan.level })} • {clan.member_count}/{clan.max_members}
          </p>
        </div>
        {getRoleBadge(clan.my_role || 'member')}
      </div>

      {/* Tabs */}
      <div className="clan-tabs">
        <button
          className={`clan-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <HiShieldCheck size={14} />
          <span>{t('clans.dashboard.tabOverview')}</span>
        </button>
        <button
          className={`clan-tab ${activeTab === 'members' ? 'active' : ''}`}
          onClick={() => setActiveTab('members')}
        >
          <HiUserGroup size={14} />
          <span>{t('clans.dashboard.tabMembers')}</span>
        </button>
        {canManage && (
          <button
            className={`clan-tab ${activeTab === 'requests' ? 'active' : ''}`}
            onClick={() => setActiveTab('requests')}
          >
            <HiPlus size={14} />
            <span>{t('clans.dashboard.tabRequests')}</span>
            {requests.length > 0 && <span className="badge">{requests.length}</span>}
          </button>
        )}
        <button
          className={`clan-tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          <HiCog6Tooth size={14} />
        </button>
      </div>

      {/* Tab Content */}
      <div className="clan-tab-content">
        {activeTab === 'overview' && (
          <div className="clan-overview">
            <div className="clan-stats-grid">
              <div className="stat-card">
                <span className="stat-value">{clan.xp}</span>
                <span className="stat-label">{t('clans.dashboard.totalXP')}</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">{clan.my_contribution_xp || 0}</span>
                <span className="stat-label">{t('clans.dashboard.yourContribution')}</span>
              </div>
            </div>
            {clan.description && (
              <p className="clan-description">{clan.description}</p>
            )}
            <div className="clan-status">
              <span className={`status-indicator ${clan.recruitment_open ? 'open' : 'closed'}`}>
                {clan.recruitment_open ? t('clans.dashboard.recruiting') : t('clans.dashboard.closed')}
              </span>
            </div>
          </div>
        )}

        {activeTab === 'members' && (
          <div className="clan-members-list">
            {loadingMembers ? (
              <div className="loading-state">{t('clans.dashboard.loadingMembers')}</div>
            ) : (
              members.map((m) => (
                <div key={m.id} className="member-row">
                  <div className="member-info">
                    <span className="member-name">{m.display_name || m.username}</span>
                    <span className="member-meta">
                      {m.odyssey_title} • {m.contribution_xp} XP
                    </span>
                  </div>
                  {getRoleBadge(m.role)}
                  {isLeader && m.user_id !== userId && (
                    <div className="member-actions">
                      {m.role === 'member' && (
                        <button
                          className="btn-icon"
                          title={t('clans.dashboard.promoteOfficer')}
                          onClick={() => handleChangeRole(m.id, 'officer')}
                        >
                          <HiChevronUp size={16} />
                        </button>
                      )}
                      {m.role === 'officer' && (
                        <>
                          <button
                            className="btn-icon"
                            title={t('clans.dashboard.demoteMember')}
                            onClick={() => handleChangeRole(m.id, 'member')}
                          >
                            <HiChevronDown size={16} />
                          </button>
                          <button
                            className="btn-icon transfer"
                            title={t('clans.dashboard.transferLeadership')}
                            onClick={() => handleTransfer(m.user_id, m.display_name || m.username)}
                          >
                            <HiArrowPath size={16} />
                          </button>
                        </>
                      )}
                      <button
                        className="btn-icon danger"
                        title={t('clans.dashboard.kickMember')}
                        onClick={() => handleKick(m.id, m.display_name || m.username)}
                      >
                        <HiTrash size={16} />
                      </button>
                    </div>
                  )}
                  {isOfficer && !isLeader && m.role === 'member' && m.user_id !== userId && (
                    <div className="member-actions">
                      <button
                        className="btn-icon danger"
                        title={t('clans.dashboard.kickMember')}
                        onClick={() => handleKick(m.id, m.display_name || m.username)}
                      >
                        <HiTrash size={16} />
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'requests' && canManage && (
          <div className="clan-requests-list">
            {loadingRequests ? (
              <div className="loading-state">{t('clans.dashboard.loadingRequests')}</div>
            ) : requests.length === 0 ? (
              <div className="empty-state">{t('clans.dashboard.noRequests')}</div>
            ) : (
              requests.map((r) => (
                <div key={r.id} className="request-row">
                  <div className="request-info">
                    <span className="request-name">{r.display_name || r.username}</span>
                    <span className="request-meta">{r.odyssey_title} • {t('clans.dashboard.level', { level: r.odyssey_level })}</span>
                    {r.message && <p className="request-message">"{r.message}"</p>}
                  </div>
                  <div className="request-actions">
                    <button
                      className="btn-icon success"
                      title={t('clans.dashboard.approve')}
                      onClick={() => handleRequest(r.id, 'approve')}
                    >
                      <HiCheck size={18} />
                    </button>
                    <button
                      className="btn-icon danger"
                      title={t('clans.dashboard.reject')}
                      onClick={() => handleRequest(r.id, 'reject')}
                    >
                      <HiXMark size={18} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="clan-settings">
            <ClanSettings
              clan={clan}
              token={token}
              isLeader={isLeader}
              onUpdated={onClanUpdated}
              showAlert={showAlert}
            />
            <div className="settings-divider" />
            <button className="btn-leave-clan" onClick={handleLeave}>
              <HiArrowRightOnRectangle size={16} />
              {isLeader ? t('clans.dashboard.dissolveClan') : t('clans.dashboard.leaveClan')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// CLAN SETTINGS - Edit clan info
// ============================================================================
interface ClanSettingsProps {
  clan: Clan;
  token: string;
  isLeader: boolean;
  onUpdated: () => void;
  showAlert: (type: AlertType, title: string, message: string, onConfirm?: () => void) => void;
}

function ClanSettings({ clan, token, isLeader, onUpdated, showAlert }: ClanSettingsProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(clan.name);
  const [description, setDescription] = useState(clan.description);
  const [recruitmentOpen, setRecruitmentOpen] = useState(clan.recruitment_open);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!isLeader) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/clans/${clan.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: name !== clan.name ? name : undefined,
          description: description !== clan.description ? description : undefined,
          recruitment_open: recruitmentOpen !== clan.recruitment_open ? recruitmentOpen : undefined,
        }),
      });
      if (res.ok) {
        setEditing(false);
        onUpdated();
        showAlert('success', t('clans.settings.saved'), t('clans.settings.savedMsg'));
      } else {
        const err = await res.json();
        showAlert('error', t('clans.errorGeneric'), err.error || t('clans.settings.errorSaving'));
      }
    } catch (err) {
      console.error('[ClanSettings] Error saving:', err);
    } finally {
      setSaving(false);
    }
  };

  if (!isLeader) {
    return (
      <div className="settings-readonly">
        <p className="settings-note">{t('clans.settings.leaderOnly')}</p>
      </div>
    );
  }

  return (
    <div className="clan-settings-form">
      {!editing ? (
        <button className="btn-edit-settings" onClick={() => setEditing(true)}>
          <HiPencil size={14} />
          {t('clans.settings.editClan')}
        </button>
      ) : (
        <>
          <div className="form-group">
            <label>{t('clans.settings.nameLabel')}</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={30}
            />
          </div>
          <div className="form-group">
            <label>{t('clans.settings.descLabel')}</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={200}
              rows={2}
            />
          </div>
          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={recruitmentOpen}
                onChange={(e) => setRecruitmentOpen(e.target.checked)}
              />
              {t('clans.settings.recruitmentOpen')}
            </label>
          </div>
          <div className="form-actions">
            <button
              className="btn-cancel"
              onClick={() => {
                setEditing(false);
                setName(clan.name);
                setDescription(clan.description);
                setRecruitmentOpen(clan.recruitment_open);
              }}
            >
              {t('clans.settings.cancel')}
            </button>
            <button className="btn-submit" onClick={handleSave} disabled={saving}>
              {saving ? t('clans.settings.saving') : t('clans.settings.save')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// CLAN RECRUITMENT SLIDE PANEL - External modal positioned next to InfiniChat
// ============================================================================
interface ClanRecruitmentSlidePanelProps {
  posts: RecruitmentPost[];
  onClose: () => void;
  chatWidth: number; // Width of InfiniChat to position correctly
}

export function ClanRecruitmentSlidePanel({ posts, onClose, chatWidth }: ClanRecruitmentSlidePanelProps) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const { token } = useAuthStore();

  // MadBoat Alert System for this panel
  const [alertState, setAlertState] = useState<AlertState>(initialAlertState);

  const showAlert = useCallback((type: AlertType, title: string, message: string, onConfirm?: () => void) => {
    setAlertState({
      show: true,
      type,
      title,
      message,
      onConfirm,
      onCancel: () => {},
    });
  }, []);

  const closeAlert = useCallback(() => {
    setAlertState(initialAlertState);
  }, []);

  const filteredPosts = posts.filter(post =>
    post.clan_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.clan_tag.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle join request
  const handleJoinClan = async (clanId: string, clanName: string) => {
    if (!token) return;

    showAlert('confirm', t('clans.joinClanTitle'), t('clans.joinClanConfirm', { name: clanName }), async () => {
      try {
        const response = await fetch(`https://mkey-api-production.fidelismadboat.workers.dev/clans/${clanId}/join`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ message: '' }),
        });

        if (response.ok) {
          showAlert('success', t('clans.requestSentTitle'), t('clans.requestSentMsg'));
        } else {
          const error = await response.json();
          showAlert('error', t('clans.errorGeneric'), error.error || t('clans.joinError'));
        }
      } catch (error) {
        console.error('[ClanRecruitment] Error joining clan:', error);
        showAlert('error', t('clans.errorGeneric'), t('clans.connectionError'));
      }
    });
  };

  return (
    <>
      {/* Backdrop */}
      <div className="clan-recruitment-external-backdrop" onClick={onClose} />

      {/* Panel positioned next to InfiniChat */}
      <div
        className="clan-recruitment-external-panel"
        style={{ left: `${15 + chatWidth + 8}px` }} // 15px = InfiniChat left position, 8px = gap
      >
        {/* Header */}
        <div className="recruitment-external-header">
          <div className="header-title">
            <HiMagnifyingGlass size={22} />
            <h2>{t('clans.searchClan')}</h2>
          </div>
          <button className="btn-close-external" onClick={onClose}>
            <HiXMark size={22} />
          </button>
        </div>

        {/* Search Bar */}
        <div className="recruitment-external-search">
          <HiMagnifyingGlass size={18} className="search-icon" />
          <input
            type="text"
            placeholder={t('clans.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
          />
        </div>

        {/* Results */}
        <div className="recruitment-external-body">
          {filteredPosts.length === 0 ? (
            <div className="empty-search-external">
              <HiShieldCheck size={64} className="empty-icon" />
              <h3>{t('clans.noClanFound')}</h3>
              <p>
                {posts.length === 0
                  ? t('clans.noClanRecruiting')
                  : t('clans.tryOtherTerms')}
              </p>
            </div>
          ) : (
            <div className="recruitment-external-grid">
              {filteredPosts.map(post => (
                <div key={post.id} className="recruitment-external-card">
                  <div className="card-top">
                    <div className="clan-emblem-external">
                      <HiShieldCheck size={32} />
                    </div>
                    <div className="clan-info-external">
                      <h3 className="clan-name-external">
                        <span className="clan-tag-badge">[{post.clan_tag}]</span>
                        {post.clan_name}
                      </h3>
                      <div className="clan-meta-external">
                        <span className="meta-item">
                          <HiUserGroup size={14} />
                          {post.member_count}/{post.max_members}
                        </span>
                        <span className="meta-item level">{t('clans.levelShort', { level: post.clan_level })}</span>
                      </div>
                    </div>
                  </div>

                  <h4 className="post-title-external">{post.title}</h4>
                  <p className="post-description-external">{post.description}</p>

                  {post.requirements && (
                    <div className="requirements-box-external">
                      <strong>{t('clans.requirements')}</strong>
                      <span>{post.requirements}</span>
                    </div>
                  )}

                  <div className="card-actions-external">
                    {post.discord_link && (
                      <a
                        href={post.discord_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-discord-external"
                      >
                        Discord
                      </a>
                    )}
                    <button
                      className="btn-request-join-external"
                      onClick={() => handleJoinClan(post.clan_id, post.clan_name)}
                    >
                      {t('clans.requestJoin')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="recruitment-external-footer">
          <span>{t('clans.clansFound', { count: filteredPosts.length } as any)}</span>
        </div>
      </div>

      {/* MadBoat Alert */}
      <MadBoatAlert alert={alertState} onClose={closeAlert} />
    </>
  );
}
